import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { AuthShell } from './Register';

/**
 * VerifyEmail — one-shot handler for the tokenized link in the
 * verification email. On mount we POST the token to /auth/verify-email;
 * a successful response sets the session_token cookie server-side, so
 * we refresh the auth context and route the user straight into the
 * child-picker (matches the Google OAuth landing behavior).
 */
export default function VerifyEmail() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { checkAuth } = useApp();
  const token = sp.get('token') || '';
  const [state, setState] = useState('verifying'); // 'verifying'|'ok'|'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!token) { setState('error'); setMessage('Missing verification token.'); return; }
      try {
        await api.post('/auth/verify-email', { token });
        if (ignore) return;
        await checkAuth();
        setState('ok');
        setTimeout(() => nav('/profiles'), 900);
      } catch (e) {
        if (ignore) return;
        setState('error');
        setMessage(e?.response?.data?.detail || 'Could not verify your email.');
      }
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthShell>
      <div className="text-center" data-testid={`verify-email-${state}`}>
        {state === 'verifying' && (
          <>
            <Loader2 size={40} className="animate-spin text-[#FF6B1A] mx-auto mb-4"/>
            <h1 className="text-2xl font-extrabold text-[#2B2140]">Verifying your email…</h1>
          </>
        )}
        {state === 'ok' && (
          <>
            <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-4"/>
            <h1 className="text-2xl font-extrabold text-[#2B2140]">You're in!</h1>
            <p className="text-sm text-slate-600 mt-2">Taking you to your reader profile…</p>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle size={44} className="text-rose-500 mx-auto mb-4"/>
            <h1 className="text-2xl font-extrabold text-[#2B2140]">Verification failed</h1>
            <p className="text-sm text-slate-600 mt-2">{message}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link data-testid="verify-back-signin" to="/login" className="pushable bg-[#FF6B1A] text-white font-bold px-5 py-2 rounded-full text-sm">Back to sign in</Link>
              <Link data-testid="verify-signup-again" to="/register" className="pushable bg-white border-2 border-[#FF6B1A] text-[#FF6B1A] font-bold px-5 py-2 rounded-full text-sm">Sign up again</Link>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}
